import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import CriticalPathScene from "./CriticalPathScene";

export const Hero = () => {
  const scrollRef = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const max = window.innerHeight * 1.5;
      scrollRef.current = Math.min(1, window.scrollY / max);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      id="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#020510] grid-bg"
    >
      {/* Cosmos backdrop with looping video */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.35] mix-blend-screen"
          poster="https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=2000&q=70"
        >
          <source
            src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2732_1440_30fps.mp4"
            type="video/mp4"
          />
        </video>
        <img
          src="https://images.unsplash.com/photo-1462331940025-496dfbfc7564?auto=format&fit=crop&w=2000&q=70"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.18] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020510]/55 via-[#020510]/30 to-[#020510]" />
        {/* Construction silhouette at bottom edge */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-x-0 bottom-0 h-[45%] w-full object-cover opacity-[0.12] mix-blend-luminosity"
        >
          <source
            src="https://videos.pexels.com/video-files/8721918/8721918-uhd_2560_1440_24fps.mp4"
            type="video/mp4"
          />
        </video>
      </div>

      {/* 3D Background */}
      <div className="absolute inset-0 z-0">
        <CriticalPathScene scrollRef={scrollRef} />
      </div>

      {/* Vignette overlay to keep text legible */}
      <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,rgba(2,5,16,0.15)_30%,rgba(2,5,16,0.9)_88%)]" />

      {/* Corner technical marks */}
      <div className="pointer-events-none absolute top-24 left-6 lg:left-10 z-20 hidden md:flex items-center gap-3 font-mono-accent text-[10px] text-[#4A6E8C]">
        <span className="w-2 h-2 border border-[#00E5FF]" />
        N-04 / CRITICAL PATH NETWORK
      </div>
      <div className="pointer-events-none absolute top-24 right-6 lg:right-10 z-20 hidden md:flex items-center gap-3 font-mono-accent text-[10px] text-[#4A6E8C]">
        REV 02.26 / BLUELINE.SYS
        <span className="w-2 h-2 bg-[#FFB300]" />
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-40 lg:pt-44 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-10 h-px bg-[#FFB300]" />
          <span className="font-mono-accent text-[11px] text-[#FFB300]">
            Project Scheduling × Project Controls
          </span>
        </motion.div>

        <motion.h1
          data-testid="hero-headline"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="font-heading text-4xl sm:text-5xl lg:text-7xl text-[#F1F5F9] max-w-5xl leading-[1.02]"
        >
          Clarity,{" "}
          <span className="text-[#00E5FF]">control</span>, and{" "}
          <span className="relative inline-block">
            confidence
            <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-[#FFB300]" />
          </span>
          <br />
          <span className="text-[#94A3B8]">on every project.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-10 text-base lg:text-lg text-[#94A3B8] max-w-2xl leading-relaxed"
        >
          BlueLine Control Systems builds the scheduling backbone for
          construction, infrastructure, and engineering projects — translating
          complexity into baseline schedules, critical path insight, and
          executive-grade reporting that drives on-time delivery.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("contact")}
            className="btn-amber"
          >
            Request Proposal <ArrowRight size={14} />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("services")}
            className="btn-ghost"
          >
            Explore Services
          </button>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1E2A3B] border border-[#1E2A3B] max-w-4xl"
        >
          {[
            { v: "P6 / MSP", l: "Schedule Stack" },
            { v: "ISO 21508", l: "Controls Aligned" },
            { v: "EVM", l: "Reporting Ready" },
            { v: "AACE", l: "Best Practice" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="bg-[#04070D] px-6 py-5"
            >
              <div className="font-heading text-2xl text-[#F1F5F9]">{s.v}</div>
              <div className="font-mono-accent text-[10px] text-[#4A6E8C] mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-[#4A6E8C]"
      >
        <span className="font-mono-accent text-[10px]">scroll</span>
        <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
